/**
 * useHistoricoReservaciones — hook dedicado al endpoint paginado del histórico.
 *
 * A diferencia de `useReservaciones` (que trae hasta 500 reservas de todas las
 * categorías en un solo fetch), este hook consulta el endpoint
 * `/turismo/reservaciones/historico` acotado por rango de fechas + agencia +
 * estados. El rango de fechas actúa como paginador natural — no hay Next/Prev.
 *
 * Cuando el operador no ha definido un rango explícito, el backend impone un
 * techo de 12 meses y devuelve `truncated: true` para que la UI lo avise.
 */
import { useQuery } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders, getEmpresaId } from './format';

async function fetchHistorico({ empresaId, desde, hasta, agenciaId, estados, q }) {
  const params = new URLSearchParams({ empresa_id: empresaId });
  if (desde) params.append('desde', desde);
  if (hasta) params.append('hasta', hasta);
  if (agenciaId && agenciaId !== 'todas') params.append('agencia_id', agenciaId);
  if (estados && estados.length) params.append('estados', estados.join(','));
  if (q && q.trim().length >= 2) params.append('q', q.trim());
  const res = await fetch(`${API_BASE}/turismo/reservaciones/historico?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Error cargando histórico (${res.status})`);
  return res.json();
}

/**
 * Retorna { reservaciones, total, truncated, rango_efectivo, isLoading, refetch, error }.
 * Los parámetros son opcionales — sin `desde`/`hasta` el backend aplica techo de 12 meses.
 * Cuando `q` viene con ≥2 caracteres, el backend ignora el rango y busca en toda
 * la BD por código, cliente, whatsapp, correo o documento.
 */
export function useHistoricoReservaciones({ desde, hasta, agenciaId, estados, q: qText } = {}) {
  const empresaId = getEmpresaId();
  // Ordenamos estados para que el cache key sea estable independientemente del
  // orden con que llegan del padre (evita re-fetch innecesario).
  const estadosKey = (estados || []).slice().sort().join(',');
  // Normalizamos q para el cache key — trim + <2 caracteres cuentan como vacío.
  const qKey = (qText || '').trim();
  const qNorm = qKey.length >= 2 ? qKey : '';
  const q = useQuery({
    queryKey: ['historico', empresaId, desde || 'x', hasta || 'x', agenciaId || 'todas', estadosKey, qNorm],
    queryFn: () => fetchHistorico({ empresaId, desde, hasta, agenciaId, estados, q: qNorm }),
    staleTime: 30 * 1000,
    keepPreviousData: true,   // evita flash de "cargando" al cambiar filtros
  });
  return {
    reservaciones: q.data?.reservaciones || [],
    total: q.data?.total || 0,
    truncated: q.data?.truncated || false,
    rangoEfectivo: q.data?.rango_efectivo || null,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error,
    refetch: q.refetch,
  };
}
